import { useState } from 'react';
import clsx from 'clsx';
import { CalendarClock, ChevronDown, Layers, Package, Smartphone, Timer, User } from '@/components/icons';
import { deviceLabel, type Platform } from '@1qa/shared';
import type { Run, TestResult } from '@/lib/types';
import { formatDateTime, formatDuration, runCounts, runDuration } from '@/lib/format';
import { Donut, STATUS_COLORS } from '@/components/charts/Donut';
import { Card, CardHeader, EmptyState, StatusPill } from '@/components/ui';
import { ArtifactGallery } from './ArtifactGallery';
import { failureOf } from '@/lib/failures';
import { describeStep } from '@/features/builder/describe';
import { FailureBreakdown, FailurePanel, FailureReasonBadge } from './FailureReason';
import { StepList } from './StepList';

function ResultRow({
  result,
  platform,
  publicToken,
  defaultOpen,
  onDeleteArtifact,
}: {
  result: TestResult;
  platform?: Platform;
  publicToken?: string;
  defaultOpen?: boolean;
  onDeleteArtifact?: (resultId: string, name: string) => void;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const failure = failureOf(result);
  const failedStep = failure?.stepIndex !== undefined ? result.steps?.[failure.stepIndex] : undefined;
  const hasDetails = !!(result.steps?.length || result.error || result.log || result.artifacts?.length);
  const panelId = `result-${result.id}`;

  return (
    <li className="border-b border-border-subtle last:border-0">
      <button
        type="button"
        onClick={() => hasDetails && setOpen((o) => !o)}
        aria-expanded={hasDetails ? open : undefined}
        aria-controls={hasDetails ? panelId : undefined}
        className={clsx('flex w-full items-center gap-4 px-5 py-3.5 text-left transition', hasDetails && 'hover:bg-surface-sunken')}
      >
        <StatusPill status={result.status} />
        <span className="min-w-0 flex-1 truncate font-medium text-fg">{result.testCase?.name ?? 'Test case'}</span>
        {failure && <FailureReasonBadge failure={failure} />}
        {result.steps?.some((s) => s.data?.some((d) => d.source === 'written')) && (
          <span
            className="hidden shrink-0 rounded-full bg-warning-subtle px-2.5 py-0.5 text-xs font-medium text-warning ring-1 ring-warning/40 ring-inset sm:inline"
            title="Some test data was missing, so values written in the test were used. Open the test to see which."
          >
            Used written values
          </span>
        )}
        {result.device && (
          <span className="hidden shrink-0 items-center gap-1 text-xs text-fg-muted md:inline-flex" title="Device this test ran on">
            <Smartphone className="size-3.5" aria-hidden /> {deviceLabel(result.device)}
          </span>
        )}
        <span className="hidden text-sm text-fg-muted tabular-nums sm:block">{formatDuration(result.durationMs)}</span>
        {hasDetails && <ChevronDown className={clsx('no-print size-3 text-fg-muted transition', open && 'rotate-180')} />}
      </button>
      {hasDetails && open && (
        <div id={panelId} className="animate-fade-in space-y-4 px-5 pb-5">
          {!!result.steps?.length && (
            <div>
              <div className="mb-1.5 text-xs font-semibold tracking-label text-fg-muted uppercase">Steps</div>
              <StepList steps={result.steps} platform={platform} failure={failure} />
            </div>
          )}
          {(result.status === 'FAILED' || result.status === 'ERROR') && result.previousStatus === 'PASSED' && (
            <div className="rounded-xl bg-amber-50 p-3.5 text-sm text-amber-900 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-100 dark:ring-amber-900">
              <div className="mb-1 font-semibold">This test passed last time</div>
              <p>It may be a flaky failure rather than a bug. Common causes:</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                <li>
                  Test data that only works once, such as signing up with the same email again. Use a fresh value like{' '}
                  <code className="font-mono text-[12px]">{'{{$randomEmail}}'}</code> or{' '}
                  <code className="font-mono text-[12px]">{'{{$uuid}}'}</code>.
                </li>
                <li>Leftovers from the earlier run (an item already in the cart, a record that already exists).</li>
                <li>A slow page: add a wait step or a check like “the dashboard is shown” before the next action.</li>
              </ul>
            </div>
          )}
          {failure && (
            <FailurePanel
              failure={failure}
              stepText={failedStep && describeStep({ id: String(failure.stepIndex), kind: failedStep.kind, value: failedStep.value }, platform)}
            />
          )}
          {result.error && result.status === 'SKIPPED' && (
            <div className="rounded-card bg-surface-sunken p-4 text-sm text-fg ring-1 ring-border ring-inset">
              <div className="mb-1 text-xs font-semibold tracking-label text-fg-muted uppercase">Why it was not run</div>
              <p className="whitespace-pre-wrap">{result.error}</p>
            </div>
          )}
          {result.error && result.status !== 'SKIPPED' && (
            <div className="rounded-card bg-danger-subtle p-4 text-sm text-danger-text ring-1 ring-danger-line ring-inset">
              <div className="mb-1 text-xs font-semibold tracking-label uppercase">{failure ? 'Error details' : 'What went wrong'}</div>
              <p className="whitespace-pre-wrap">{result.error}</p>
            </div>
          )}
          {result.artifacts?.length > 0 && (
            <ArtifactGallery
              resultId={result.id}
              artifacts={result.artifacts}
              publicToken={publicToken}
              onDelete={onDeleteArtifact && ((name) => onDeleteArtifact(result.id, name))}
            />
          )}
          {result.log && (
            <details className="group rounded-xl bg-slate-950 text-slate-200 ring-1 ring-slate-800">
              <summary className="cursor-pointer px-4 py-2.5 text-xs font-medium text-slate-400 select-none hover:text-white">Technical log</summary>
              <pre className="max-h-96 overflow-auto px-4 pb-4 font-mono text-xs leading-relaxed whitespace-pre-wrap">{result.log}</pre>
            </details>
          )}
        </div>
      )}
    </li>
  );
}

export function ReportSummary({ run }: { run: Run }) {
  const c = runCounts(run);
  const finished = c.passed + c.failed + c.errored;
  const rate = finished > 0 ? Math.round((c.passed / finished) * 100) : null;

  const stats = [
    { label: 'Passed', value: c.passed, color: STATUS_COLORS.passed },
    { label: 'Failed', value: c.failed, color: STATUS_COLORS.failed },
    { label: 'Errors', value: c.errored, color: STATUS_COLORS.errored },
    { label: 'Skipped / pending', value: c.skipped, color: STATUS_COLORS.skipped },
  ];

  return (
    <Card className="p-6">
      <div className="flex flex-col items-center gap-8 sm:flex-row">
        <Donut
          center={rate === null ? '—' : `${rate}%`}
          caption="pass rate"
          slices={[
            { label: 'Passed', value: c.passed, color: STATUS_COLORS.passed },
            { label: 'Failed', value: c.failed, color: STATUS_COLORS.failed },
            { label: 'Errors', value: c.errored, color: STATUS_COLORS.errored },
            { label: 'Skipped', value: c.skipped, color: STATUS_COLORS.skipped },
          ]}
        />
        <div className="grid flex-1 grid-cols-2 gap-3 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl bg-slate-50 p-4 dark:bg-slate-800/50">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                <span className="size-2 rounded-full" style={{ background: s.color }} />
                {s.label}
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">{s.value}</div>
            </div>
          ))}
          <div className="col-span-2 flex flex-wrap gap-x-6 gap-y-2 pt-1 text-sm text-slate-500 md:col-span-4 dark:text-slate-400">
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock className="size-4" /> {formatDateTime(run.startedAt ?? run.queuedAt)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Timer className="size-4" /> {formatDuration(runDuration(run))}
            </span>
            {run.triggeredBy?.name && (
              <span className="inline-flex items-center gap-1.5">
                <User className="size-4" /> {run.triggeredBy.name}
              </span>
            )}
            {run.collectionName && (
              <span className="inline-flex items-center gap-1.5" title="Test data collection (environment) this run used">
                <Layers className="size-4" /> {run.collectionName}
              </span>
            )}
            {run.appBuildLabel && (
              <span className="inline-flex items-center gap-1.5" title="App build this run installed on the device">
                <Package className="size-4" /> Build {run.appBuildLabel}
              </span>
            )}
          </div>
        </div>
      </div>
      <FailureBreakdown results={run.results ?? []} />
      {run.error && (
        <p className="mt-5 rounded-xl bg-amber-50 p-3.5 text-sm text-amber-900 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:ring-amber-900">
          {run.error}
        </p>
      )}
    </Card>
  );
}

export function ReportResults({
  run,
  publicToken,
  onDeleteArtifact,
}: {
  run: Run;
  publicToken?: string;
  /** Set when the viewer may delete stored evidence (e.g. a failed test's video). */
  onDeleteArtifact?: (resultId: string, name: string) => void;
}) {
  const results = run.results ?? [];
  // Failures first so the important information is on top.
  const order = { FAILED: 0, ERROR: 1, SKIPPED: 2, PASSED: 3 } as const;
  const sorted = results.slice().sort((a, b) => order[a.status] - order[b.status]);

  return (
    <Card>
      <CardHeader title="Test results" description={`${results.length} test${results.length === 1 ? '' : 's'} in this run`} />
      {sorted.length === 0 ? (
        <div className="p-5">
          <EmptyState title="No results yet" description="Results appear here as each test finishes." />
        </div>
      ) : (
        <ul>
          {sorted.map((r, i) => (
            <ResultRow
              key={r.id}
              result={r}
              platform={run.project?.platform}
              publicToken={publicToken}
              defaultOpen={i === 0 && r.status !== 'PASSED'}
              onDeleteArtifact={onDeleteArtifact}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Step-by-step status of the test executing right now (refreshed while the run is active). */
export function RunProgressCard({ run }: { run: Run }) {
  const p = run.progress;
  if (!p) return null;
  const done = p.steps.filter((s) => s.status === 'PASSED').length;
  const others = (p.running ?? []).filter((r) => r.testCaseId !== p.testCaseId);
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {others.length ? `Running now: ${others.length + 1} tests at the same time` : 'Running now'} <StatusPill status="RUNNING" />
          </span>
        }
        description={`Test ${p.index} of ${p.total}: ${p.name} - ${done} of ${p.steps.length} step${p.steps.length === 1 ? '' : 's'} done`}
      />
      {others.length > 0 && (
        <p className="border-b border-slate-100 px-5 pb-3 text-sm text-slate-500 dark:border-slate-800">
          Also running: {others.map((r) => `${r.index}. ${r.name}`).join(', ')}
        </p>
      )}
      <div className="p-5" aria-live="polite">
        <StepList steps={p.steps} platform={run.project?.platform} />
      </div>
    </Card>
  );
}
